import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';

const FAQS = [
  {
    q: 'Why do preferred stocks have 5-letter tickers ending in P?',
    a: 'US exchanges use a 5th letter to flag special share classes, and P is the most common convention for preferred stock (STRRP, WVVIP, FGBIP). It is a convention, not a law \u2014 some platforms use O, N or M for additional preferred series, and brokers render the same security as STRR-A, STRR.PR.A or STRRp. The first four letters usually point at the issuer\u2019s root ticker, but always confirm through the company\u2019s SEC CIK number because the mapping is not guaranteed.',
  },
  {
    q: 'What is par value on a preferred stock?',
    a: 'The face value the dividend is calculated on and the amount you are owed if the company redeems the shares or liquidates \u2014 commonly $25.00, sometimes $10.00 (STRRP) or another figure. A \u201c10% Series A\u201d preferred with a $10 par pays $1.00 a year no matter what the market price does. Buying below par raises your effective yield, buying above par lowers it and adds call risk.',
  },
  {
    q: 'What does cumulative vs non-cumulative mean?',
    a: 'Cumulative means missed dividends pile up as \u201cdividends in arrears\u201d and the company is legally blocked from paying common shareholders a cent until preferred holders are made whole. Non-cumulative means a skipped payment is gone forever \u2014 common with bank preferreds, because regulators require it for the shares to count as bank capital. The word is always in the security\u2019s official name, which is where DiviDrip reads it from.',
  },
  {
    q: 'What does "callable" mean and why does it matter?',
    a: 'After a stated call date the issuer can redeem the shares at par with roughly 30 days notice. If you paid $10.50 for a $10.00-par preferred that gets called, you eat an instant $0.50 loss per share and your income stream stops. The flip side: buying below par past the call date means a call hands you a small capital gain. Call terms live in the original prospectus (SEC form 424B5) under \u201cOptional Redemption.\u201d',
  },
  {
    q: 'Are preferred dividends qualified for tax purposes?',
    a: 'Often yes \u2014 most traditional C-corp and bank preferred dividends are qualified (15-20% federal rate) if you meet the holding-period rules. Two big exceptions: REIT preferreds pay ordinary-income dividends just like REIT commons, and anything structured as debt (baby bonds, ELN coupons) pays interest taxed at your full marginal rate. Check the issuer\u2019s 1099 classification history before assuming.',
  },
];

export default function LearnPreferredTickers() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  return (
    <LearnPage
      title="Decoding Preferred Tickers — The 5th-Letter P, Par Value, Call Dates & Cumulative Dividends | DiviDrip"
      description="Why tickers like STRRP and WVVIP end in P, how par value and call dates work, cumulative vs non-cumulative dividends, and how to trace a preferred back to its common stock."
      canonical="https://twylightcrow.com/learn/preferred-stock-tickers"
      guidePath="/learn/preferred-stock-tickers"
      sectionLabel="Vehicles & Structures"
      sectionAnchor="vehicles"
      kicker="Learn · Share Classes"
      h1="Decoding Preferred Tickers &mdash; The Fixed-Income Shares Hiding Behind a 5th Letter"
      datePublished="2026-08-29"
      jsonLd={jsonLd}
      related={[
        { to: '/learn/preferred-stocks-explained', label: 'Preferred Stocks vs Common — when 6-10% preferreds beat common' },
        { to: '/learn/treasury-vehicle-preferreds', label: 'Treasury-Vehicle Preferreds — when normal stock math stops working' },
        { to: '/learn/autocallable-eln-etfs', label: 'Autocallable ELN ETFs — ARKY and structured-note income' },
        { to: '/learn/qualified-vs-ordinary-dividends', label: 'Qualified vs ordinary dividends — tax guide' },
      ]}
    >
      <h2 id="what-is-preferred">Half stock, half bond</h2>
      <p>
        <strong>Preferred stock</strong> is a separate share class that sits between a
        company&rsquo;s bonds and its common stock. It pays a <strong>contractual, fixed
        dividend</strong> calculated on a stated par value, gets paid before any common
        dividend, and ranks ahead of common shareholders if the company liquidates. In
        exchange you give up voting rights and almost all of the upside &mdash; a preferred
        trades like a bond, drifting around its par value with interest rates rather than
        following the business&rsquo;s growth. For the case that preferreds deserve a slice
        of an income portfolio at all, see our{' '}
        <Link to="/learn/preferred-stocks-explained">preferred vs common guide</Link>{' '}
        &mdash; this one is about reading the securities themselves.
      </p>
      <p>
        Two live examples from the DiviDrip database: <strong>STRRP</strong> &mdash; Star
        Equity Holdings 10% Series A Cumulative Perpetual Preferred, $10.00 par, paying
        $0.25 quarterly &mdash; and <strong>WVVIP</strong> &mdash; Willamette Valley
        Vineyards Series A Redeemable Preferred, which pays $0.22 <em>once a year</em>{' '}
        each December (and famously lets wine-club shareholders take tasting-room perks
        alongside the cash).
      </p>

      <h2 id="fifth-letter">Decoding the 5th letter</h2>
      <p>
        US exchanges append a 5th letter to flag special share classes, and{' '}
        <strong>P is the standard mark for preferred stock</strong>. The first four
        letters usually point at the issuer&rsquo;s root: STRRP belongs to Star Equity
        (common ticker <strong>STRR</strong>), WVVIP to Willamette Valley Vineyards
        (<strong>WVVI</strong>). Three warnings before you trust that pattern:
      </p>
      <ul>
        <li>
          <strong>The convention is loose.</strong> Additional series often use O, N or M
          (AGNCN, TCBIO, MBNKO are all preferreds), and your broker may render the same
          security as STRR-A, STRR.PR.A or STRRp.
        </li>
        <li>
          <strong>Roots can mislead.</strong> Strategy&rsquo;s preferreds (STRK, STRF,
          STRD, STRC) look nothing like their common ticker MSTR &mdash; letter-dropping
          would point you at the wrong company entirely.
        </li>
        <li>
          <strong>The CIK number is the truth.</strong> Preferred and common shares are
          issued by the same legal entity, so they share one SEC CIK. DiviDrip resolves
          the parent this way &mdash; the Preferred badge in the stock modal shows a{' '}
          <strong>Common:</strong> chip that jumps straight to the issuer&rsquo;s common
          stock.
        </li>
      </ul>

      <h2 id="cumulative">Cumulative vs non-cumulative &mdash; the safety clause</h2>
      <p>
        This single word in the security&rsquo;s name decides what happens when the
        issuer hits hard times:
      </p>
      <ul>
        <li>
          <strong>Cumulative</strong> &mdash; skipped dividends accrue as{' '}
          <strong>dividends in arrears</strong>. The company cannot pay a penny to common
          shareholders until every missed preferred payment is made whole. STRRP is
          cumulative.
        </li>
        <li>
          <strong>Non-cumulative</strong> &mdash; a skipped payment is{' '}
          <strong>lost forever</strong>. Bank preferreds (Bank OZK&rsquo;s OZKAP, Valley
          National&rsquo;s VLYPP) are almost always non-cumulative because regulators
          require it for the shares to count toward bank capital.
        </li>
      </ul>
      <p>
        DiviDrip reads this straight from the official security name and shows it inside
        the Preferred badge when known.
      </p>

      <h2 id="callable">Par value, call dates, and the premium trap</h2>
      <p>
        Most preferreds are <strong>callable</strong>: after a protection window (typically
        five years from issue), the company may redeem the shares at par with about 30
        days notice &mdash; the same logic as refinancing an expensive mortgage. The
        risk math is simple:
      </p>
      <ul>
        <li>
          <strong>Trading above par + past the call date = danger.</strong> Pay $10.50
          for a $10.00-par preferred and a surprise call hands you an instant loss.
        </li>
        <li>
          <strong>Trading below par + past the call date = a small bonus.</strong> STRRP
          around $9.91 against a $10.00 par means a call would actually pay you $0.09
          per share on top of accrued dividends.
        </li>
        <li>
          <strong>The terms live in SEC EDGAR.</strong> Search the issuer&rsquo;s 424B5
          prospectus supplement for &ldquo;Optional Redemption&rdquo; &mdash; that section
          states the exact call date and redemption price. Redemption notices themselves
          arrive later as 8-K filings.
        </li>
      </ul>

      <h2 id="yield-to-call">Yield to call &amp; fixed-to-floating resets</h2>
      <p>
        Current yield (annual dividend &divide; today&rsquo;s price) is only half the story
        for a callable preferred. <strong>Yield to call</strong> asks: if the issuer redeems
        at par on the call date, what did I actually earn per year? It adds the
        &ldquo;pull to par&rdquo; &mdash; the gap between your price and par, spread over the
        years left &mdash; to the coupons:
      </p>
      <p className="font-mono text-sm">
        YTC &asymp; (annual dividend + (par &minus; price) &divide; years to call) &divide; ((par + price) &divide; 2)
      </p>
      <ul>
        <li>
          <strong>Below par:</strong> yield to call is <em>higher</em> than current yield
          &mdash; a $24.74 price on a $25 par adds a small gain when redeemed.
        </li>
        <li>
          <strong>Above par:</strong> yield to call is <em>lower</em> &mdash; the premium
          you paid evaporates at the call. The further above par and the closer the
          call date, the harder the hit.
        </li>
        <li>
          <strong>Already callable (or &ldquo;issuer&rsquo;s choice&rdquo;):</strong> there
          are no coupons left to count on, so the honest number is the instant gain or
          loss if the shares were redeemed <em>today</em>. DiviDrip labels these
          &ldquo;now&rdquo;.
        </li>
      </ul>
      <p>
        <strong>Fixed-to-floating</strong> preferreds (AGNCN, AGNCO, VLYPP) add a twist:
        the coupon is fixed only through the first call date, then resets each quarter to
        a benchmark &mdash; three-month SOFR (the old LIBOR + a 0.26161% adjustment) or a
        Treasury yield &mdash; plus a spread stated in the prospectus. Once floating, the
        payout rises and falls with the Fed, not with the issuer&rsquo;s earnings. The
        Asset Class card in the Stock Modal shows the fixed coupon, the reset benchmark and
        the spread; the{' '}
        <Link to="/screener?is_preferred=only" data-testid="learn-pref-screener-link">Preferred Shares screener view</Link>{' '}
        lines every listed preferred up by par, coupon, price-vs-par, call date,
        cumulative / fixed-to-floating type and a sortable yield-to-call column, so the
        above-par names that would lose money on a call stand out immediately.
      </p>

      <h2 id="screeners">Why screeners mangle preferred data</h2>
      <p>
        Preferred shares break the assumptions most stock tools are built on. P/E, EPS
        growth and payout ratio describe the <em>issuer&rsquo;s common stock</em>, not the
        preferred contract. Annual payers like WVVIP get shown as &ldquo;0% yield&rdquo;
        for eleven months by apps that assume quarterly cadence. And thin liquidity makes
        price charts gappy. DiviDrip&rsquo;s cadence detection handles the annual
        schedule, the Preferred badge flags the share class, and treasury-style preferreds
        (SATA, STRK, STRD) get their own{' '}
        <Link to="/learn/treasury-vehicle-preferreds">separate treatment</Link>{' '}
        because their dividends are funded by capital raises rather than operating
        earnings.
      </p>

      <h2 id="faq">FAQ</h2>
      {FAQS.map(({ q, a }) => (
        <details key={q} className="learn-faq mt-4 cursor-help">
          <summary className="cursor-help">{q}</summary>
          <p>{a}</p>
        </details>
      ))}
    </LearnPage>
  );
}
